import { ChevronDown, ChevronUp, LogIn, LogOut, RotateCcw, X } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type FC, type ReactNode } from 'react';

import { useAuth } from '@/hooks/useAuth';
import {
  useSettings,
  ZOOM_OFFSET_DEFAULT,
  ZOOM_OFFSET_MAX,
  ZOOM_OFFSET_MIN,
  type SpeedUnit,
} from '@/hooks/useSettings';
import { signOut } from '@/lib/auth';
import type { GravelMode, RouteMode } from '@/lib/routing';

interface Props {
  onClose: () => void;
}

/** Collapsible settings group — collapsed by default so the sheet opens short. */
const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="mg-settings-section">
      <button className="mg-settings-section-header" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{title}</span>
        {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {open && <div className="mg-settings-section-body">{children}</div>}
    </section>
  );
};

/** Labelled on/off switch row, sized for gloved taps. */
const ToggleRow: FC<{
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <div className="mg-setting-row">
    <span className="mg-setting-label">{label}</span>
    <label className="mg-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="mg-switch-track" />
      <span className="mg-switch-thumb" />
    </label>
  </div>
);

/** Labelled segmented-control row (speed unit, route defaults). */
function SegmentRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="mg-setting-row">
      <span className="mg-setting-label">{label}</span>
      <div className="mg-segment" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={value === o.value ? 'mg-segment-btn active' : 'mg-segment-btn'}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const SPEED_UNITS: { value: SpeedUnit; label: string }[] = [
  { value: 'kph', label: 'km/h' },
  { value: 'mph', label: 'mph' },
];

const ROUTE_MODES: { value: RouteMode; label: string }[] = [
  { value: 'fast', label: 'Fast' },
  { value: 'scenic', label: 'Scenic' },
  { value: 'curvy', label: 'Curvy' },
];

const ROUTE_GRAVEL: { value: GravelMode; label: string }[] = [
  { value: 'exclude', label: 'Avoid' },
  { value: 'neutral', label: 'Allow' },
  { value: 'include', label: 'Seek' },
];

const ACCOUNT_BOX: CSSProperties = {
  marginTop: 20,
  paddingTop: 16,
  borderTop: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const ACCOUNT_INFO: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const ELLIPSIS: CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function formatTick(n: number): string {
  if (n > 0) return `+${n}`;
  return `${n}`;
}

const SettingsModal: FC<Props> = ({ onClose }) => {
  const { settings, setZoomOffset, updateSettings } = useSettings();
  const { state, exitGuestMode } = useAuth();
  const user = state.status === 'authenticated' ? state.user : null;
  const guestName = state.status === 'guest' ? state.name : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="mg-backdrop" onClick={onClose}>
      <div className="mg-sheet" role="dialog" aria-modal="true" aria-label="Settings" onClick={(e) => e.stopPropagation()}>
        <div className="mg-sheet-handle" />
        <div className="mg-sheet-header">
          <div className="mg-sheet-title">Settings</div>
          <button className="mg-icon-btn" onClick={onClose} title="Close" aria-label="Close settings">
            <X size={22} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', minHeight: 0 }}>
          <Section title="Map">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label htmlFor="zoom-offset" style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                  Map zoom
                </label>
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--accent)',
                    fontWeight: 700,
                    minWidth: 32,
                    textAlign: 'right',
                  }}
                >
                  {formatTick(settings.zoomOffset)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="zoom-offset"
                  className="mg-slider"
                  type="range"
                  min={ZOOM_OFFSET_MIN}
                  max={ZOOM_OFFSET_MAX}
                  step={1}
                  value={settings.zoomOffset}
                  onChange={(e) => setZoomOffset(Number(e.target.value))}
                />
                <button
                  className="mg-icon-btn"
                  onClick={() => setZoomOffset(ZOOM_OFFSET_DEFAULT)}
                  title="Reset to default"
                  aria-label="Reset map zoom to default"
                >
                  <RotateCcw size={18} />
                </button>
              </div>
            </div>

            <ToggleRow
              label="Speed-based zoom"
              checked={settings.speedZoom}
              onChange={(v) => updateSettings({ speedZoom: v })}
            />
            <ToggleRow
              label="Show group members"
              checked={settings.showPeers}
              onChange={(v) => updateSettings({ showPeers: v })}
            />
            <ToggleRow
              label="Lock map to north"
              checked={settings.lockNorth}
              onChange={(v) => updateSettings({ lockNorth: v })}
            />
            <SegmentRow
              label="Speed unit"
              options={SPEED_UNITS}
              value={settings.speedUnit}
              onChange={(v) => updateSettings({ speedUnit: v })}
            />
          </Section>

          <Section title="Route planner">
            {/* The defaults every planning/editing session starts from — using the planner
                no longer overwrites them, so "fast + allow gravel" stays the baseline. */}
            <SegmentRow
              label="Default style"
              options={ROUTE_MODES}
              value={settings.routeMode}
              onChange={(v) => updateSettings({ routeMode: v })}
            />
            <SegmentRow
              label="Gravel"
              options={ROUTE_GRAVEL}
              value={settings.routeGravel}
              onChange={(v) => updateSettings({ routeGravel: v })}
            />
          </Section>

          <Section title="Recording">
            <ToggleRow
              label="Auto-record rides"
              checked={settings.autoRecord}
              onChange={(v) => updateSettings({ autoRecord: v })}
            />

            {settings.autoRecord && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label htmlFor="split-gap" style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                    New ride after stop of
                  </label>
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--accent)',
                      fontWeight: 700,
                      minWidth: 56,
                      textAlign: 'right',
                    }}
                  >
                    {settings.rideSplitGapMin} min
                  </span>
                </div>
                <input
                  id="split-gap"
                  className="mg-slider"
                  type="range"
                  min={5}
                  max={120}
                  step={5}
                  value={settings.rideSplitGapMin}
                  onChange={(e) => updateSettings({ rideSplitGapMin: Number(e.target.value) })}
                />
              </div>
            )}
          </Section>
        </div>

        {user && (
          <div style={ACCOUNT_BOX}>
            <div style={ACCOUNT_INFO}>
              <div style={{ ...ELLIPSIS, fontWeight: 600 }}>{user.name}</div>
              <div style={{ ...ELLIPSIS, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                {user.email}
              </div>
            </div>
            <button
              className="mg-btn mg-btn-outline"
              style={{ width: 'auto', minHeight: 44, padding: '10px 14px', fontSize: '0.9rem' }}
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        )}

        {guestName && (
          <div style={ACCOUNT_BOX}>
            <div style={ACCOUNT_INFO}>
              <div style={{ ...ELLIPSIS, fontWeight: 600 }}>{guestName}</div>
              <div style={{ ...ELLIPSIS, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                Some features need an account
              </div>
            </div>
            <button
              className="mg-btn mg-btn-outline"
              style={{ width: 'auto', minHeight: 44, padding: '10px 14px', fontSize: '0.9rem' }}
              onClick={exitGuestMode}
              title="Sign in to your account"
              aria-label="Sign in to your account"
            >
              <LogIn size={16} />
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsModal;
